import React from "react";
import type {OnNodeDrag, XYPosition} from "@xyflow/react";
import {useReactFlow} from "@xyflow/react";
import type {TaxonomyCanvasNode, TaxonomyCanvasNodeData} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";
import {NODE_HEIGHT, NODE_WIDTH} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";

export type DropAction = 'merge' | 'move';

export type DropRequest = {
    action: DropAction
    source: {id: number, label: string, childCount: number}
    target: {id: number, label: string}
}

/**
 * Same level → merge into the target. One level up (and not the current parent) → move under the target.
 * Anything else is not a valid drop.
 */
export function dropActionFor(source: TaxonomyCanvasNodeData, target: TaxonomyCanvasNodeData, targetId: number): DropAction | null {
    if (target.level === source.level) return 'merge';
    if (target.level === source.level - 1 && targetId !== source.parentId) return 'move';
    return null;
}

type DragState = {
    id: string
    position: XYPosition
    targetId: string | null
    action: DropAction | null
}

/**
 * Drag a category onto another one to merge / move it. The dragged card follows the pointer; on release it
 * snaps back and `onDrop` is called when the drop is valid (the caller confirms and applies it).
 */
export function useTaxonomyDrag(nodes: TaxonomyCanvasNode[], onDrop: (request: DropRequest) => void) {
    const {screenToFlowPosition} = useReactFlow();
    const [drag, setDrag] = React.useState<DragState | null>(null);
    // Read the latest nodes from a ref so the handlers below keep their identity (see TaxonomyCanvas)
    const nodesRef = React.useRef(nodes);
    React.useLayoutEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    // The target is the card under the pointer (not the dragged card's overlap)
    const findDrop = React.useCallback((event: MouseEvent | TouchEvent, node: TaxonomyCanvasNode) => {
        const point = 'changedTouches' in event ? event.changedTouches[0] : event;
        if (!point) return {target: undefined, action: null};
        const p = screenToFlowPosition({x: point.clientX, y: point.clientY});
        const target = nodesRef.current.find(n => n.id !== node.id
            && p.x >= n.position.x && p.x <= n.position.x + NODE_WIDTH
            && p.y >= n.position.y && p.y <= n.position.y + NODE_HEIGHT);
        const action = target ? dropActionFor(node.data, target.data, Number(target.id)) : null;
        return {target: action ? target : undefined, action};
    }, [screenToFlowPosition]);

    const onNodeDrag = React.useCallback<OnNodeDrag<TaxonomyCanvasNode>>((event, node) => {
        const {target, action} = findDrop(event, node);
        setDrag({id: node.id, position: node.position, targetId: target?.id ?? null, action});
    }, [findDrop]);

    const onNodeDragStop = React.useCallback<OnNodeDrag<TaxonomyCanvasNode>>((event, node) => {
        const {target, action} = findDrop(event, node);
        setDrag(null);
        if (!target || !action) return;
        onDrop({
            action,
            source: {id: Number(node.id), label: node.data.label, childCount: node.data.childCount},
            target: {id: Number(target.id), label: target.data.label},
        });
    }, [findDrop, onDrop]);

    return {drag, onNodeDrag, onNodeDragStop};
}
